import { AdjustInStockDialog } from '@/pages/stock/components/AdjustInStockDialog';
import { AdjustOutStockDialog } from '@/pages/stock/components/AdjustOutStockDialog';
import { Product, ProductVariant } from '@/types/product-type';
import { Loader2 } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { ProductSaleGridCard } from './product-sale-grid-card';
import { ProductSaleListCard } from './product-sale-list-card';
import { getStockSnapshot } from './product-sale-utils';
import { SalePagination } from './SalePagination';

export type ViewMode = 'grid' | 'list';

type Row = {
  p: Product;
  v: ProductVariant;
  key: string;
  variantId: string;
};

type PreparedRow = Row & {
  sold: number;
  remain: number;
  total: number;
  isPinned: boolean;
  isSelected: boolean;
};

export type ProductSaleGridViewV2Props = {
  rows: Row[];
  selectedIds: string[];
  pinnedIdSet: Set<string>;
  onToggleSelect: (id: string) => void;
  onCopyCode?: (code: string) => void;
  onSendCode?: (row: Product) => void;
  onTogglePin?: (id: string, productId: string, pinned: boolean) => void;
  onQuickSell?: (id: string, delta: number) => void;
  viewMode?: ViewMode;
  totalProduct?: number;
  limitProduct?: number;
  offsetProduct?: number;
  onRefresh?: (id: string) => void;
  onPageChange?: (offset: number) => void;
  onEditPrice?: (v: ProductVariant) => void;
  isLoadingProduct?: boolean;
  isRefreshingProduct?: boolean;
};

const noop = () => {};

export function ProductSaleGridViewV2({
  rows,
  selectedIds,
  pinnedIdSet,
  onToggleSelect,
  onTogglePin,
  viewMode = 'grid',
  totalProduct,
  limitProduct,
  offsetProduct,
  onPageChange,
  onRefresh,
  onEditPrice,
  isLoadingProduct,
  isRefreshingProduct,
}: ProductSaleGridViewV2Props) {
  const [adjustInOpen, setAdjustInOpen] = useState(false);
  const [adjustOutOpen, setAdjustOutOpen] = useState(false);
  const [variant, setVariant] = useState<ProductVariant | null>(null);

  const onAdjustIn = useCallback((v: ProductVariant) => {
    setVariant(v);
    setAdjustInOpen(true);
  }, []);

  const onAdjustOut = useCallback((v: ProductVariant) => {
    setVariant(v);
    setAdjustOutOpen(true);
  }, []);

  const handlersRef = useRef({
    onTogglePin,
    onEditPrice,
    onRefresh,
    onToggleSelect,
  });
  handlersRef.current = { onTogglePin, onEditPrice, onRefresh, onToggleSelect };

  const handleTogglePin = useCallback(
    (id: string, productId: string, pinned: boolean) =>
      handlersRef.current.onTogglePin?.(id, productId, pinned),
    [],
  );
  const handleEditPrice = useCallback(
    (v: ProductVariant) => handlersRef.current.onEditPrice?.(v),
    [],
  );

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const preparedRows = useMemo<PreparedRow[]>(
    () =>
      rows.map((row) => {
        const { sold, remain, total } = getStockSnapshot(row.v, row.p);
        return {
          ...row,
          sold,
          remain,
          total,
          isPinned: pinnedIdSet.has(row.variantId),
          isSelected: selectedIdSet.has(row.p.id),
        };
      }),
    [rows, pinnedIdSet, selectedIdSet],
  );

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center p-4">
        {isLoadingProduct ? (
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 px-8 py-10 text-sm text-slate-500">
            No products found.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col md:min-h-0 md:flex-1">
      <div className="relative p-2 md:min-h-0 md:flex-1 md:overflow-y-auto md:overscroll-y-contain md:p-3">
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {preparedRows.map((row) => (
              <div
                key={row.key}
                className="[content-visibility:auto] [contain-intrinsic-size:0_192px]"
              >
                <ProductSaleGridCard
                  product={row.p}
                  variant={row.v}
                  variantId={row.variantId}
                  sold={row.sold}
                  remain={row.remain}
                  isPinned={row.isPinned}
                  onTogglePin={handleTogglePin}
                  onAdjustIn={onAdjustIn}
                  onAdjustOut={onAdjustOut}
                  onEditPrice={handleEditPrice}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {preparedRows.map((row) => (
              <div
                key={row.key}
                className="[content-visibility:auto] [contain-intrinsic-size:0_88px]"
              >
                <ProductSaleListCard
                  product={row.p}
                  variant={row.v}
                  variantId={row.variantId}
                  remain={row.remain}
                  sold={row.sold}
                  total={row.total}
                  isSelected={row.isSelected}
                  isPinned={row.isPinned}
                  onToggleSelect={onToggleSelect}
                  onTogglePin={onTogglePin}
                  onRefresh={onRefresh}
                  onAdjustIn={onAdjustIn}
                  onAdjustOut={onAdjustOut}
                  onEditPrice={onEditPrice}
                />
              </div>
            ))}
          </div>
        )}

        {isRefreshingProduct && (
          <div className="pointer-events-none absolute right-2 top-2 z-10 rounded-full border border-slate-200 bg-white p-1.5 shadow-sm md:right-4 md:top-4">
            <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-slate-100 bg-white px-2 py-2 md:px-3">
        <SalePagination
          total={totalProduct ?? 0}
          limit={limitProduct ?? 200}
          offset={offsetProduct ?? 0}
          onPageChange={onPageChange ?? noop}
        />
      </div>

      {variant?.stock && (
        <>
          <AdjustInStockDialog
            isOpen={adjustInOpen}
            onClose={() => setAdjustInOpen(false)}
            onSuccess={() => onRefresh?.(variant.id)}
            stock={variant.stock}
            sku={variant.sku}
          />
          <AdjustOutStockDialog
            isOpen={adjustOutOpen}
            onClose={() => setAdjustOutOpen(false)}
            onSuccess={() => onRefresh?.(variant.id)}
            stock={variant.stock}
            sku={variant.sku}
          />
        </>
      )}
    </div>
  );
}
